import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Key, Plus, Copy, Trash2, Eye, EyeOff, Loader2, Webhook, Check, Terminal, Lock, Activity } from 'lucide-react';
import { apiKeysAPI } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

export default function ApiKeys() {
  const { user } = useAuth();
  const tier = (user?.subscription_tier || 'basic').toLowerCase();
  const canCreate = ['advanced', 'enterprise'].includes(tier) || user?.role === 'admin';

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newScopes, setNewScopes] = useState({ read: true, write: false, admin: false });
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiKeysAPI.list();
      setKeys(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!canCreate) { toast.error('Upgrade to Advanced or Enterprise to create keys'); return; }
    const scopes = Object.entries(newScopes).filter(([, v]) => v).map(([k]) => k);
    if (scopes.length === 0) { toast.error('Select at least one scope'); return; }
    setCreating(true);
    try {
      const res = await apiKeysAPI.create(newName.trim() || 'Unnamed Key', scopes);
      setCreatedKey(res.data);
      setRevealed(true);
      setNewName('');
      setNewScopes({ read: true, write: false, admin: false });
      setCreateOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (key) => {
    try {
      await apiKeysAPI.delete(key);
      toast.success('API key revoked');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete key');
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="api-keys-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="cp-page-title flex items-center gap-2">
            <Key className="w-6 h-6 text-muted-foreground" />
            API Keys
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage keys for webhook integrations (n8n, SAP, DATEV, custom).
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} disabled={!canCreate} data-testid="create-api-key-btn">
          <Plus className="w-4 h-4 mr-2" /> New API Key
        </Button>
      </div>

      <div className="flex justify-end">
        <Link to="/settings/api-keys/usage" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="view-usage-link">
          <Activity className="w-4 h-4" /> View usage →
        </Link>
      </div>

      {!canCreate && (
        <Card className="p-4 bg-amber-500/10 border-amber-500/30" data-testid="upgrade-notice">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-900 dark:text-amber-200">Upgrade required to create keys</p>
              <p className="text-amber-800 dark:text-amber-300/80 mt-0.5">
                API keys for webhook integrations are available on <b>Advanced</b> and <b>Enterprise</b> plans.
                You can still view existing demo keys below.
              </p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-hidden" data-testid="api-keys-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Key Prefix</th>
                <th className="px-4 py-3 font-medium">Scopes</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium">Last Used</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </td></tr>
              ) : keys.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-muted-foreground" data-testid="no-keys-row">
                  No API keys yet. Create one to get started.
                </td></tr>
              ) : keys.map((k) => (
                <tr key={k.id} data-testid={`api-key-row-${k.prefix}`} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium">{k.name || 'Unnamed Key'}</td>
                  <td className="px-4 py-3 font-mono text-xs">{k.masked}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(k.scopes || ['read']).map((s) => (
                        <Badge key={s} variant="outline" className="font-mono text-[10px] uppercase" data-testid={`scope-badge-${s}`}>
                          {s}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(k.created_at)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(k.last_used_at)}</td>
                  <td className="px-4 py-3">
                    <Badge variant={k.active ? 'default' : 'secondary'} className={k.active ? 'bg-green-600/10 text-green-700 dark:text-green-400 border-green-600/20' : ''}>
                      {k.active ? 'Active' : 'Disabled'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive"
                                data-testid={`revoke-key-${k.prefix}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Revoke this API key?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Integrations using <span className="font-mono">{k.masked}</span> will stop working immediately.
                            This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid={`cancel-revoke-${k.prefix}`}>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(k.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid={`confirm-revoke-${k.prefix}`}>
                            Revoke key
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Integration guide */}
      <Card className="p-5" data-testid="webhook-docs-card">
        <div className="flex items-center gap-2 mb-3">
          <Webhook className="w-5 h-5 text-primary" />
          <h2 className="font-semibold">Using your API key with n8n</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Send CSV rows to the webhook endpoint with your key in the JSON body:
        </p>
        <pre className="text-xs bg-muted/50 border border-border rounded-md p-3 overflow-x-auto font-mono" data-testid="webhook-snippet">
{`POST ${process.env.REACT_APP_BACKEND_URL}/api/webhook/csv-import
Content-Type: application/json

{
  "api_key": "YOUR_API_KEY_HERE",
  "source_system": "n8n",
  "data_type": "Materials",
  "rows": [
    { "name": "Steel Rebar", "production": 120 },
    { "name": "Cement Grade A", "production": 340 }
  ]
}`}
        </pre>
        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
          <Terminal className="w-3.5 h-3.5" />
          Rejects with 401 if key is missing or revoked.
        </div>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-key-dialog">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>Give it a descriptive name so you can identify it later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="key-name">Key name</Label>
              <Input id="key-name" data-testid="create-key-name-input" value={newName}
                     onChange={(e) => setNewName(e.target.value)} placeholder="e.g. SAP S/4HANA – Production" />
            </div>
            <div className="space-y-2">
              <Label>Scopes</Label>
              <p className="text-xs text-muted-foreground">What this key is allowed to do. You can grant multiple.</p>
              <div className="grid grid-cols-1 gap-2">
                {[
                  { id: 'read',  label: 'read',  desc: 'Read-only access to PCF, reports and product data.' },
                  { id: 'write', label: 'write', desc: 'Allow POST imports (e.g. /webhook/csv-import).' },
                  { id: 'admin', label: 'admin', desc: 'Full access including admin ops. Admin users only.' },
                ].map((sc) => (
                  <label key={sc.id} className="flex items-start gap-2 p-2 rounded border border-border hover:bg-muted/30 cursor-pointer" data-testid={`scope-${sc.id}-row`}>
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-primary"
                      checked={newScopes[sc.id]}
                      onChange={(e) => setNewScopes((s) => ({ ...s, [sc.id]: e.target.checked }))}
                      data-testid={`scope-${sc.id}-checkbox`}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-mono font-medium">{sc.label}</div>
                      <div className="text-xs text-muted-foreground">{sc.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="create-key-cancel-btn">Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="create-key-confirm-btn">
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal created key dialog (shown once) */}
      <Dialog open={!!createdKey} onOpenChange={(open) => { if (!open) { setCreatedKey(null); setRevealed(false); } }}>
        <DialogContent data-testid="reveal-key-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key className="w-5 h-5 text-primary" /> Your new API key</DialogTitle>
            <DialogDescription className="text-destructive">
              This is the only time the full key will be displayed. Copy and store it securely.
            </DialogDescription>
          </DialogHeader>
          {createdKey && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Name</Label>
                <div className="text-sm font-medium">{createdKey.name}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">API Key</Label>
                <div className="flex items-center gap-2 p-3 bg-muted/50 border border-border rounded-md font-mono text-sm break-all" data-testid="revealed-key-value">
                  <span className="flex-1">
                    {revealed ? createdKey.key : '•'.repeat(Math.min(createdKey.key.length, 32))}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setRevealed((r) => !r)} data-testid="toggle-reveal-btn">
                    {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleCopy(createdKey.key)} data-testid="copy-key-btn">
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { setCreatedKey(null); setRevealed(false); }} data-testid="close-reveal-btn">
              I've saved my key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
